import type { Breadcrumb } from '../types';
import type { State } from '../core/state';
import { describeElement, now } from '../core/util';

/**
 * Click / input-name / navigation / visibility breadcrumbs (PLAN.md §3.3). Values are never recorded.
 * `onRouteChange` also fires for SPA route changes, so the UI can re-check page visibility rules.
 */
export function installBreadcrumbs(state: State, onRouteChange?: (url: string) => void): () => void {
  const add = (b: Omit<Breadcrumb, 'at'>) => state.breadcrumbs.push({ ...b, at: now() });

  const onClick = (e: MouseEvent) => {
    const el = (e.target as Element | null)?.closest?.('button, a, [role=button], input[type=submit], label, summary') ?? (e.target as Element | null);
    if (!el || isOurs(el)) return;
    const text = (el.textContent ?? '').trim().replace(/\s+/g, ' ').slice(0, 100);
    add({ type: 'click', target: describeElement(el), message: text || undefined });
  };

  const onChange = (e: Event) => {
    const el = e.target as HTMLInputElement | null;
    if (!el || isOurs(el) || !('tagName' in el)) return;
    add({ type: 'input', target: describeElement(el) });
  };

  const onVisibility = () => add({ type: 'visibility', message: document.visibilityState });

  let lastUrl = location.href;
  const onNavigate = () => {
    const next = location.href;
    if (next === lastUrl) return;
    add({ type: 'navigation', from: lastUrl, to: next });
    lastUrl = next;
    onRouteChange?.(next);
  };

  document.addEventListener('click', onClick, true);
  document.addEventListener('change', onChange, true);
  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('popstate', onNavigate);
  window.addEventListener('hashchange', onNavigate);

  const origPush = history.pushState;
  const origReplace = history.replaceState;
  history.pushState = function (...args) {
    origPush.apply(this, args);
    onNavigate();
  };
  history.replaceState = function (...args) {
    origReplace.apply(this, args);
    onNavigate();
  };

  return () => {
    document.removeEventListener('click', onClick, true);
    document.removeEventListener('change', onChange, true);
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('popstate', onNavigate);
    window.removeEventListener('hashchange', onNavigate);
    history.pushState = origPush;
    history.replaceState = origReplace;
  };
}

export function isOurs(el: Element | null): boolean {
  return !!el && (el.id === 'fetrace-root' || !!el.closest?.('#fetrace-root'));
}
